import React, { useEffect, useState } from 'react';
import { Card, CardContent } from './ui/Card';
import { Button } from './ui/Button';
import { BrainIcon, ZapIcon, ShieldCheckIcon, EyeIcon, ActivityIcon, ScaleIcon, CheckCircle2Icon } from 'lucide-react';
import { useScrollReveal } from '../hooks/useScrollReveal';
import { InteractiveTerminal } from './InteractiveTerminal';
export function PricingSection() {
  const revealRef = useScrollReveal();
  const [animated, setAnimated] = useState(false);
  useEffect(() => {
    const observer = new IntersectionObserver(([entry]) => {
      if (entry.isIntersecting) {
        setAnimated(true);
        observer.disconnect();
      }
    }, {
      threshold: 0.3
    });
    const bars = document.getElementById('pricing-progress-bars');
    if (bars) observer.observe(bars);
    return () => observer.disconnect();
  }, []);
  const progressBars = [{
    label: 'Presnosť',
    value: '97%',
    width: '97%'
  }, {
    label: 'Rýchlosť',
    value: '10x',
    width: '85%'
  }, {
    label: 'Pokrytie',
    value: '94%',
    width: '94%'
  }];
  const nodeCards = [{
    title: 'OmniMem',
    description: 'Digitálna pamäť vašej firmy. AI spojí e-maily, poznámky z meetingov a dokumenty do jedného celku — nájdete čokoľvek za sekundu.',
    icon: <BrainIcon className="w-6 h-6 text-apple-blue" data-id="element-367" />,
    gradient: 'from-blue-50 to-transparent',
    image: 'https://images.unsplash.com/photo-1532619675605-1ede6c2ed2b0?w=600&h=300&fit=crop&crop=center'
  }, {
    title: 'VibeStyle AI',
    description: 'Vytvorte vizuály, ktoré sedia vašej značke. AI pozná váš štýl a automaticky prispôsobí každý výstup tak, aby vyzeral profesionálne.',
    icon: <EyeIcon className="w-6 h-6 text-purple-500" data-id="element-368" />,
    gradient: 'from-purple-50 to-transparent',
    image: 'https://images.unsplash.com/photo-1561070791-2526d30994b5?w=600&h=300&fit=crop&crop=center'
  }, {
    title: 'EchoVision Pro',
    description: 'Preklad reality pre nevidiacich. Priestorový zvuk vytvorený z obrazu scény — používateľ „počuje" prekážky, ľudí aj objekty okolo seba.',
    icon: <ZapIcon className="w-6 h-6 text-amber-500" data-id="element-369" />,
    gradient: 'from-amber-50 to-transparent',
    image: 'https://images.unsplash.com/photo-1573164713714-d95e436ab8d6?w=600&h=300&fit=crop&crop=center'
  }, {
    title: 'SkillStream',
    description: 'Učenie remesiel cez kameru a hlas. AI sleduje vaše ruky a krok za krokom vám hovorí, čo robiť — ako keby vám majster stál za chrbtom.',
    icon: <ActivityIcon className="w-6 h-6 text-emerald-500" data-id="element-370" />,
    gradient: 'from-emerald-50 to-transparent',
    image: 'https://images.unsplash.com/photo-1581092160562-40aa08e78837?w=600&h=300&fit=crop&crop=center'
  }, {
    title: 'MediMatch',
    description: 'Rýchla zdravotná kontrola z fotky a zvuku. Odfotíte vyrážku, nahráte kašeľ — AI pomôže lekárovi s prvotným vyhodnotením na diaľku.',
    icon: <ShieldCheckIcon className="w-6 h-6 text-rose-500" data-id="element-371" />,
    gradient: 'from-rose-50 to-transparent',
    image: 'https://images.unsplash.com/photo-1576091160399-112ba8d25d1d?w=600&h=300&fit=crop&crop=center'
  }, {
    title: 'LawLens',
    description: 'Odfotíte zmluvu a spýtate sa: „Čo ak prestanem platiť?" AI vám jednoducho vysvetlí, čo to znamená — bez právnického žargónu.',
    icon: <ScaleIcon className="w-6 h-6 text-indigo-500" data-id="element-372" />,
    gradient: 'from-indigo-50 to-transparent',
    image: 'https://images.unsplash.com/photo-1589829545856-d10d557cf95f?w=600&h=300&fit=crop&crop=center'
  }];
  const compactItems = ['07. Diagnostika áut podľa zvuku motora', '08. Recepty z pohľadu do chladničky', '09. Sledovanie nálady podľa hlasu', '10. Preklad s porozumením gest a emócií', '11. Filmová hudba z hmkania melódie', '12. Domáca bezpečnosť, ktorá chápe situácie', '13. Hľadanie autentických miest podľa zvukov', '14. Analýza mimiky pri online predaji', '15. Nakresli appku na papier — AI ju vytvorí', '16. Automatické triedenie fotiek z eventov', '17. Vizuálna kontrola dodávateľského reťazca', '18. Diskusia s videoprednáškou v reálnom čase', '19. Skladový manažment cez fotoaparát', '20. Analýza pohybu pre športovcov'];
  return <section id="portfolio" ref={revealRef} className="py-36 px-4 sm:px-6 lg:px-8 overflow-hidden" data-id="element-373">
      <div className="max-w-7xl mx-auto" data-id="element-374">
        {/* 1. SECTION HEADING */}
        <div className="reveal text-center mb-36" data-id="element-375">
          <h2 className="text-5xl md:text-7xl font-extrabold tracking-tight text-apple-black" data-id="element-376">
            20 Produktov. Jeden Engine. Vaša Príležitosť.
          </h2>
        </div>

        {/* 2. GEMINI 2 HERO INTRO BLOCK */}
        <div className="reveal max-w-4xl mx-auto text-center mb-28" data-id="element-377">
          <p className="text-xs font-semibold tracking-[0.2em] text-apple-gray uppercase mb-8" data-id="element-378">
            AI Platforma Novej Generácie
          </p>
          <h3 className="text-4xl md:text-6xl font-extrabold tracking-tight text-apple-black mb-8 leading-tight" data-id="element-379">
            Čo všetko dokážeme postaviť
          </h3>
          <p className="text-lg md:text-xl text-apple-gray leading-relaxed max-w-3xl mx-auto mb-10 font-light" data-id="element-380">
            Každý z týchto 20 produktov je reálny projekt, ktorý môžeme dodať.
            Nie teória, nie koncept — fungujúce PWA aplikácie postavené na
            Gemini 2 engine. Vyberte si ten, ktorý rieši váš problém, alebo nám
            povedzte svoj nápad.
          </p>
          <div className="flex flex-wrap justify-center gap-4" data-id="element-381">
            <div className="bg-apple-blue/10 text-apple-blue border border-apple-blue/30 rounded-full px-6 py-2.5 font-semibold text-sm shadow-[0_0_25px_rgba(0,113,227,0.15)]" data-id="element-382">
              PWA Ready
            </div>
            <div className="bg-apple-blue/10 text-apple-blue border border-apple-blue/30 rounded-full px-6 py-2.5 font-semibold text-sm shadow-[0_0_25px_rgba(0,113,227,0.15)]" data-id="element-383">
              20 Hotových Konceptov
            </div>
          </div>
        </div>

        {/* 3. ANIMATED PROGRESS BARS */}
        <div id="pricing-progress-bars" className="reveal max-w-3xl mx-auto mb-36 space-y-8" data-id="element-384">
          {progressBars.map((bar, index) => <div key={index} className="w-full" data-id="element-385">
              <div className="flex justify-between items-end mb-3" data-id="element-386">
                <span className="text-base font-semibold text-apple-black" data-id="element-387">
                  {bar.label}
                </span>
                <span className="text-lg font-bold text-apple-blue" data-id="element-388">
                  {bar.value}
                </span>
              </div>
              <div className="h-2.5 w-full bg-gray-100 rounded-full overflow-hidden" data-id="element-389">
                <div className="h-full bg-gradient-to-r from-apple-blue to-blue-400 rounded-full" style={{
              width: animated ? bar.width : '0%',
              transition: `width 1.2s cubic-bezier(0.16, 1, 0.3, 1) ${index * 0.2}s`
            }} data-id="element-390" />
              </div>
            </div>)}
        </div>

        {/* 4. NESTAVIAME PROTOTYPY SECTION */}
        <div className="reveal mb-36" data-id="element-391">
          <h3 className="text-3xl md:text-5xl font-extrabold tracking-tight text-apple-black text-center mb-16" data-id="element-392">
            Nestaviame prototypy. Staviame hotové riešenia.
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 max-w-4xl mx-auto" data-id="element-393">
            <Card className="border-gray-200 rounded-3xl bg-apple-light/50 border-none shadow-sm card-hover-premium" data-id="element-394">
              <CardContent className="p-10" data-id="element-395">
                <div className="w-14 h-14 bg-white rounded-2xl flex items-center justify-center shadow-md mb-6" data-id="element-396">
                  <ShieldCheckIcon className="w-7 h-7 text-apple-blue" data-id="element-397" />
                </div>
                <h4 className="text-2xl font-bold text-apple-black mb-4" data-id="element-398">
                  Kvalita
                </h4>
                <p className="text-apple-gray leading-relaxed font-light" data-id="element-399">
                  Aplikácie, ktoré vyzerajú a fungujú bezchybne. Dostupnosť
                  99.99% znamená, že vaše riešenie beží spoľahlivo — aj keď ho
                  používajú tisíce ľudí naraz.
                </p>
              </CardContent>
            </Card>
            <Card className="border-gray-200 rounded-3xl bg-apple-light/50 border-none shadow-sm card-hover-premium" data-id="element-400">
              <CardContent className="p-10" data-id="element-401">
                <div className="w-14 h-14 bg-white rounded-2xl flex items-center justify-center shadow-md mb-6" data-id="element-402">
                  <ZapIcon className="w-7 h-7 text-apple-blue" data-id="element-403" />
                </div>
                <h4 className="text-2xl font-bold text-apple-black mb-4" data-id="element-404">
                  Rýchlosť
                </h4>
                <p className="text-apple-gray leading-relaxed font-light" data-id="element-405">
                  Kľúčové funkcie dodávame v 14-dňových cykloch. Odozva pod 50
                  milisekúnd znamená, že všetko reaguje okamžite — bez čakania,
                  bez zasekávania.
                </p>
              </CardContent>
            </Card>
          </div>
        </div>

        {/* 5. MARKET ANALYSIS CALLOUT */}
        <div className="reveal max-w-4xl mx-auto text-center mb-28" data-id="element-406">
          <h4 className="text-xs font-semibold tracking-[0.2em] text-apple-gray uppercase mb-6" data-id="element-407">
            Na čom práve pracujeme?
          </h4>
          <p className="text-2xl md:text-3xl font-medium text-apple-black leading-snug" data-id="element-408">
            Vytvárame aplikácie šité na mieru pre konkrétne odvetvia — od
            zdravotníctva cez logistiku až po právo. Nie všeobecné nástroje, ale
            riešenia, ktoré presne rozumejú vášmu svetu a vašim potrebám.
          </p>
        </div>

        {/* 6. GRID OF 6 PRIMARY NODE CARDS */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 max-w-6xl mx-auto mb-28" data-id="element-409">
          {nodeCards.map((node, index) => <div key={index} className={`reveal reveal-delay-${Math.min(index + 1, 5)}`} data-id="element-410">
              <Card className="border-gray-200 rounded-3xl overflow-hidden group card-hover-premium h-full" data-id="element-411">
                <div className="h-44 relative overflow-hidden" data-id="element-412">
                  <img src={node.image} alt={node.title} className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-700" data-id="element-413" />
                  <div className={`absolute inset-0 bg-gradient-to-t ${node.gradient} opacity-60`} data-id="element-414"></div>
                  <div className="absolute bottom-4 left-4 w-12 h-12 bg-white/90 backdrop-blur-sm rounded-xl flex items-center justify-center shadow-md" data-id="element-415">
                    {node.icon}
                  </div>
                </div>
                <CardContent className="p-8" data-id="element-416">
                  <h5 className="text-xl font-bold text-apple-black mb-3" data-id="element-417">
                    {node.title}
                  </h5>
                  <p className="text-apple-gray text-sm leading-relaxed font-light" data-id="element-418">
                    {node.description}
                  </p>
                </CardContent>
              </Card>
            </div>)}
        </div>

        {/* 7. COMPACT LIST OF ITEMS 07-20 */}
        <div className="reveal max-w-4xl mx-auto mb-36" data-id="element-419">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-12 gap-y-4" data-id="element-420">
            {compactItems.map((item, index) => <div key={index} className="flex items-center gap-3 py-3 border-b border-gray-100 last:border-0 sm:last:border-b sm:[&:nth-last-child(-n+2)]:border-0 hover:bg-gray-50/50 rounded-lg px-2 -mx-2 transition-colors" data-id="element-421">
                <CheckCircle2Icon className="w-4 h-4 text-apple-blue/40 shrink-0" data-id="element-422" />
                <span className="text-sm font-medium text-apple-gray" data-id="element-423">
                  {item}
                </span>
              </div>)}
          </div>
        </div>

        {/* NEW CTA BLOCK */}
        <div id="kontakt" className="reveal-scale max-w-4xl mx-auto text-center mb-36 bg-gradient-to-br from-apple-light via-white to-apple-light rounded-[3rem] p-14 md:p-24 border border-gray-200 shadow-lg" data-id="element-424">
          <h3 className="text-3xl md:text-5xl font-extrabold tracking-tight text-apple-black mb-8" data-id="element-425">
            Postavíme Váš Globálny Hit?
          </h3>
          <p className="text-xl text-apple-gray leading-relaxed max-w-2xl mx-auto mb-12 font-light" data-id="element-426">
            Tento zoznam je len začiatok. Máme technológiu, máme tím a máme
            víziu. Hľadáme partnerov, ktorí chcú ovládnuť trh pomocou
            multimodálnej AI.
          </p>
          <div className="flex flex-col sm:flex-row items-center justify-center gap-4" data-id="element-427">
            <Button size="large" className="!bg-apple-blue hover:!bg-apple-blueHover !text-white !rounded-full !px-10 !py-4 !font-bold w-full sm:w-auto btn-hover-premium !border-none" data-id="element-428">
              Začať Konzultáciu
            </Button>
            <Button size="large" className="!bg-white hover:!bg-gray-50 !text-apple-black !border-2 !border-gray-200 !rounded-full !px-10 !py-4 !font-bold w-full sm:w-auto card-hover-premium" data-id="element-429">
              Naše Portfólio
            </Button>
          </div>
        </div>

        {/* 8. INTERACTIVE TERMINAL */}
        <div className="reveal-scale" data-id="element-430">
          <InteractiveTerminal data-id="element-431" />
        </div>

        {/* 9. MARKET POTENTIAL STAT */}
        <div className="reveal text-center pb-16" data-id="element-432">
          <p className="text-xs font-semibold tracking-[0.2em] text-apple-gray uppercase mb-6" data-id="element-433">
            Veľkosť Príležitosti
          </p>
          <div className="text-7xl md:text-9xl font-extrabold tracking-tighter text-transparent bg-clip-text bg-gradient-to-b from-apple-black to-gray-400" data-id="element-434">
            $12.4B
          </div>
        </div>
      </div>
    </section>;
}